/**
 * «Назначение посева» — чистая логика подбора целей выращивания.
 *
 * Одна культура сеется под разные задачи: тритикале «на зелёный корм»,
 * озимая пшеница «на семена» или «на товарное зерно». Раньше такие вещи
 * заводили отдельными культурами — теперь это отдельное поле посева.
 *
 * Какие назначения допустимы для культуры — знает справочник
 * (reference.crop_growing_goals, приходит в детали культуры как
 * `growing_goal_ids`). Если для культуры список не задан (или деталь не
 * подгрузилась) — НЕ блокируем пользователя, показываем весь справочник.
 */

/** Опция назначения для селектора (нормализованная из reference-ответа). */
export interface GrowingGoalOption {
  id: string;
  code: string;
  name: string;
}

/**
 * Отфильтровать справочник назначений под выбранную культуру.
 *
 * `allowedIds`:
 *  · `undefined` / `null` — данных о культуре нет (не выбрана, ещё грузится,
 *    запрос упал) → показываем все назначения;
 *  · `[]` — у культуры не настроено ни одного назначения → тоже показываем все
 *    (пустой список в справочнике почти всегда означает «не заполнили», а не
 *    «запрещено всё»);
 *  · непустой массив — оставляем пересечение.
 */
export function allowedGoalOptions(
  all: GrowingGoalOption[],
  allowedIds: string[] | null | undefined,
): GrowingGoalOption[] {
  if (!allowedIds || allowedIds.length === 0) return all;
  const allow = new Set(allowedIds);
  const filtered = all.filter((g) => allow.has(g.id));
  // Справочник целей и связка культуры разошлись (цель архивирована) —
  // не оставляем пользователя с пустым селектором.
  return filtered.length > 0 ? filtered : all;
}

/**
 * Дополнить список опций уже сохранёнными назначениями посева.
 *
 * Зачем: справочник культуры мог измениться ПОСЛЕ того, как посев сохранили
 * («на семена» убрали из допустимых для пшеницы). Молча потерять то, что
 * агроном уже ввёл, нельзя — показываем такое назначение в списке, пока
 * культуру не меняли.
 */
export function withSavedGoals(
  options: GrowingGoalOption[],
  saved: Array<{ id: string; code: string; name_ru: string }>,
): GrowingGoalOption[] {
  const known = new Set(options.map((o) => o.id));
  const extra = saved
    .filter((g) => !known.has(g.id))
    .map((g) => ({ id: g.id, code: g.code, name: g.name_ru }));
  return extra.length > 0 ? [...options, ...extra] : options;
}

/**
 * Снять из выбора те назначения, которых нет среди доступных опций.
 * Возвращает ИСХОДНЫЙ массив, если снимать нечего — чтобы вызов из useEffect
 * не порождал новый объект и бесконечный re-render.
 */
export function pruneSelectedGoals(
  selected: string[],
  options: GrowingGoalOption[],
): string[] {
  const allow = new Set(options.map((o) => o.id));
  const next = selected.filter((id) => allow.has(id));
  return next.length === selected.length ? selected : next;
}

/** Человеческое имя назначения из name_translations (ru → code → id). */
export function goalDisplayName(raw: {
  id: string;
  code?: string | null;
  name_translations?: Record<string, string> | null;
}): string {
  return raw.name_translations?.ru || raw.code || raw.id;
}
